// src/components/mitchRoom/revenueAnalytics/ChurnTable.js
import React from 'react';
import { TrendingDown } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Badge } from 'components/ui/badge';
import { Skeleton } from 'components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { formatCurrency, formatPeriod, formatPercent } from 'utils/format';

// Monthly churn rate thresholds (%): below `good` is healthy, below `warning`
// needs watching, anything above is a problem. Adjust here if the business
// benchmark changes.
const CHURN_THRESHOLDS = { good: 3, warning: 5 };

const getChurnColor = (rate) => {
  if (rate < CHURN_THRESHOLDS.good) return '#59A14F';
  if (rate < CHURN_THRESHOLDS.warning) return '#F28E2B';
  return '#E15759';
};

const getChurnVariant = (rate) => {
  if (rate < CHURN_THRESHOLDS.good) return 'success';
  if (rate < CHURN_THRESHOLDS.warning) return 'warning';
  return 'destructive';
};

// 'YYYY-MM' -> 'July 2026' (tooltip only; axis/table use formatPeriod)
const formatLongPeriod = (period) => {
  if (typeof period !== 'string' || !period.includes('-')) return String(period ?? '');
  const [year, month] = period.split('-');
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const gbpWhole = (value) =>
  value === null || value === undefined ? '–' : formatCurrency(value, { whole: true });

export default function ChurnTable({ churn, loading }) {
  if (loading && !churn) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-3 text-token-xl font-semibold">Churn Analysis</h3>
        <div className="flex flex-col gap-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!churn || !churn.data || churn.data.length === 0) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-3 text-token-xl font-semibold">Churn Analysis</h3>
        <p className="py-6 text-center text-token-md text-muted-foreground">
          No churn data available
        </p>
      </div>
    );
  }

  // Prepare chart data (last 12 months)
  const chartData = churn.data.slice(-12);

  const avgRate = churn.average_churn_rate ?? 0;
  // Compounded, not rate x 12 — the linear form reads 60% where the truth is
  // 46%. Served by the API; fall back to computing it here for older payloads.
  const annualisedRate =
    churn.annualised_churn_rate ?? (1 - (1 - avgRate / 100) ** 12) * 100;

  return (
    <div className="flex flex-col gap-4">
      {/* Summary Cards */}
      <div className="flex flex-wrap gap-3">
        <div className="min-w-[180px] flex-1 rounded-token-large border border-borderGrey bg-white p-4">
          <p className="text-token-sm text-muted-foreground">Average Churn Rate</p>
          <p
            className="text-token-2xl font-semibold"
            style={{ color: getChurnColor(avgRate) }}
          >
            {formatPercent(avgRate)}
          </p>
        </div>
        <div className="min-w-[180px] flex-1 rounded-token-large border border-borderGrey bg-white p-4">
          <p className="text-token-sm text-muted-foreground">Total Churned</p>
          <p className="text-token-2xl font-semibold">
            {(churn.total_churned ?? 0).toLocaleString()}
          </p>
        </div>
        <div className="min-w-[180px] flex-1 rounded-token-large border border-borderGrey bg-white p-4">
          <p className="text-token-sm text-muted-foreground">Annualised Churn (Est.)</p>
          <p
            className="text-token-2xl font-semibold"
            style={{ color: getChurnColor(avgRate) }}
          >
            {formatPercent(annualisedRate)}
          </p>
          <p className="text-token-xs text-muted-foreground">compounded: 1−(1−r)¹²</p>
        </div>
      </div>

      {/* Chart - full width */}
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-2 text-token-xl font-semibold">Monthly Cancellations</h3>
        <div className="h-[350px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DADADA" vertical={false} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={50}
                tickFormatter={formatPeriod}
              />
              <YAxis tick={{ fontSize: 12 }} width={40} />
              <Tooltip labelFormatter={formatLongPeriod} />
              <Bar dataKey="cancellations" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getChurnColor(entry.churn_rate)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly detail table */}
      <div className="overflow-hidden rounded-token-large border border-borderGrey bg-white">
        <div className="border-b border-borderGrey px-4 py-3">
          <h3 className="text-token-xl font-semibold">Monthly Details</h3>
        </div>
        <div className="max-h-[320px] overflow-auto">
          <Table className="border-separate border-spacing-0">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="sticky top-0 z-[3] border-b border-borderGrey bg-white">
                  Month
                </TableHead>
                <TableHead className="sticky top-0 z-[3] border-b border-borderGrey bg-white text-right">
                  Cancellations
                </TableHead>
                <TableHead className="sticky top-0 z-[3] border-b border-borderGrey bg-white text-right">
                  Churn Rate
                </TableHead>
                <TableHead className="sticky top-0 z-[3] border-b border-borderGrey bg-white text-right">
                  Lost MRR
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {churn.data
                .slice()
                .reverse()
                .map((row) => (
                  <TableRow key={row.period} className="border-0 hover:bg-neutral-50">
                    <TableCell className="whitespace-nowrap border-b border-borderGrey">
                      {formatPeriod(row.period)}
                    </TableCell>
                    <TableCell className="border-b border-borderGrey text-right">
                      <span className="inline-flex items-center justify-end gap-1">
                        <TrendingDown size={13} className="text-brand-destructive" aria-hidden="true" />
                        {row.cancellations}
                      </span>
                    </TableCell>
                    <TableCell className="border-b border-borderGrey text-right">
                      <Badge variant={getChurnVariant(row.churn_rate)}>
                        {formatPercent(row.churn_rate)}
                      </Badge>
                    </TableCell>
                    {/* Lost MRR is the primary business signal — same
                        traffic-light colouring as the churn rate. */}
                    <TableCell
                      className="border-b border-borderGrey text-right font-medium tabular-nums"
                      style={{ color: getChurnColor(row.churn_rate) }}
                    >
                      {gbpWhole(row.lost_mrr)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
